import type { ComponentPropsWithoutRef, ElementType } from "react";

import { cn } from "@/lib/utils";

type WorkspaceLayoutProps<T extends ElementType = "section"> = {
  as?: T;
  /**
   * When true (default), enforces a min-height that fills the viewport
   * beneath the global top bar.
   */
  fullHeight?: boolean;
} & Omit<ComponentPropsWithoutRef<T>, "as">;

const gapClassMap = {
  none: "gap-0",
  xs: "gap-[var(--space-xs)]",
  sm: "gap-[var(--space-sm)]",
  md: "gap-[var(--space-md)]",
  lg: "gap-[var(--space-lg)]",
  xl: "gap-[var(--space-xl)]",
} as const;

type WorkspaceContentProps<T extends ElementType = "div"> = {
  as?: T;
  /**
   * Controls the gap between stacked containers. Defaults to `lg`.
   */
  gap?: keyof typeof gapClassMap;
  /**
   * When true (default), allows the content region to stretch and scroll
   * within the surrounding workspace canvas.
   */
  fullHeight?: boolean;
} & Omit<ComponentPropsWithoutRef<T>, "as">;

export function WorkspaceLayout<T extends ElementType = "section">({
  as,
  fullHeight = true,
  className,
  children,
  ...rest
}: WorkspaceLayoutProps<T>) {
  const Component = (as ?? "section") as ElementType;

  return (
    <Component
      className={cn(
        "workspace-layout flex w-full items-stretch",
        // Use `dvh` (dynamic viewport height) so the layout reacts to Safari's
        // collapsing URL bar and Android's gesture bar — `vh` is the static
        // max-viewport height which over-shoots when the browser chrome is
        // visible and leaves the bottom panel hidden under the address bar.
        fullHeight &&
          "min-h-[calc(100dvh-var(--workspace-top-offset))] max-h-[calc(100dvh-var(--workspace-top-offset))] overflow-hidden",
        className,
      )}
      {...rest}
    >
      {children}
    </Component>
  );
}

export function WorkspaceContent<T extends ElementType = "div">({
  as,
  gap = "lg",
  fullHeight = true,
  className,
  children,
  ...rest
}: WorkspaceContentProps<T>) {
  const Component = (as ?? "div") as ElementType;

  return (
    <Component
      className={cn(
        "workspace-content flex min-w-0 flex-col",
        fullHeight && "flex-1 min-h-0",
        gapClassMap[gap],
        className,
      )}
      {...rest}
    >
      {children}
    </Component>
  );
}
